/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { Toaster } from 'app/shared/lib/toaster';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { deepClone } from 'app/shared/lib/utilities';
import { CaseAssignmentHistoryGrid } from 'app/cm/components/common/case-assignment-history-grid';
import { validate } from 'app/shared/elements/ess-rule';
import { I18N } from 'aurelia-i18n';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class CaseRoutingPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    private caseId!: string;
    protected readonly: boolean = false;

    protected selectedQueue: CM.CaseQueue | null = null;
    protected allQueues: ReadonlyArray<CM.CaseQueue> | null = null;
    protected queueHistory: ReadonlyArray<CM.CaseQueueHistory> | null = null;
    protected currentQueues: ReadonlyArray<CM.CaseQueueHistory> | null = null;

    protected selectedOwner: CM.CaseOwner | null = null;
    protected caseOwners: ReadonlyArray<CM.CaseOwner> | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;

    private subscriptions: Array<Subscription> = [];

    protected assignedToInvalid: boolean | null = null;

    protected hasAssignCase: boolean = false;

    protected $isAssignCaseValid: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    private $assignmentHistory!: CaseAssignmentHistoryGrid;
    protected $assignCaseForm!: IForm;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly context: CasePageContext,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate(params: { caseId: string }): Promise<boolean> {
        if(!params.caseId) { throw new Error('Missing or invalid caseId parameter'); }

        return true;
    }

    protected async activate(params: { caseId: string }): Promise<void> {
        if(this.caseId !== params.caseId) { this.caseId = params.caseId; }

        this.checkPermissions();

        this.loadQueues();
        this.loadQueueHistory();
        this.loadCaseOwners();

        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async handleCaseChangeEvent(e: CaseChangeEvent) {
        if(e.case && e.case.id === this.caseId) {
            this.checkPermissions();
            this.reload();
        };
    }

    protected applyRoutingSort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected assignedToChanged(): void {
        this.verifyAssignment();
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAssignCase = this.accountContext.hasPermission('CM.Case.Assign');
        const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');

        if(this.context && this.context.case) {
            this.readonly = this.context.caseIsReadOnly(canEditClosedCase);
        }
    }

    public async reload(): Promise<void> {
        await this.loadQueueHistory();
    }

    private async verifyAssignment(): Promise<void> {
        const saveCase = this.context.case;
        const assignedToId = this.selectedOwner?.id || '';
        if(!saveCase || !assignedToId) {
            this.assignedToInvalid = null;
            return;
        }

        try {
            this.state.beginProcessing();

            const response = await this.service.verifyAssignment(assignedToId, [getReference(saveCase)!]);

            // make sure "assignedTo" hasn't changed while awaiting response
            if(this.selectedOwner?.id !== assignedToId) {
                this.logger.info(this.i18n.tr('ui:messages.warnings.verificationInterruptionDiscardResults'));
            }
            else {
                this.assignedToInvalid = null;
                if(response.isSuccessful && response.result.filter && response.result.filter.items) {
                    // if no items come back, assigned to is invalid
                    this.assignedToInvalid = (response.result.filter.items.length === 0);
                }
            }
        }
        finally {
            this.state.endProcessing();
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadQueues(): Promise<void> {
        try {
            this.state.beginLoad();
            const fields: string[] = ['description'];
            const response = await this.service.loadQueues({ all: true }, [], fields);
            this.allQueues = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadQueueHistory(): Promise<void> {
        if(!this.caseId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes: string[] = [
            'metadata.started.byUser',
            'metadata.ended.byUser',
            'queue'
        ];

        const fields: string[] = [
            'queue.description',
            'metadata.started.byUser.description',
            'metadata.started.timestamp',
            'metadata.ended.byUser.description',
            'metadata.ended.timestamp',
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadQueueHistoryByCase(this.caseId, options, includes, fields);
            this.queueHistory = response.results;
            this.resultCount = response.count || 0;

            this.currentQueues = this.queueHistory.filter(q => {
                return !(q.metadata && q.metadata.ended && q.metadata.ended.timestamp)
            });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async route(): Promise<void> {
        if(!this.selectedQueue) { return; }

        if(this.currentQueues && this.currentQueues.some(q => (q.queue && q.queue.id) === (this.selectedQueue && this.selectedQueue.id))) {
            this.toaster.warn().message("Case already is in the selected queue").show();
            return;
        }

        const $case: CM.Case = {
            id: this.caseId,
            queues: getReferences([this.selectedQueue])
        }

        try {
            this.state.beginSave();
            const response = await this.service.routeToQueue($case);
            this.toaster.standard.routed('cm:case.$', response.isSuccessful);
            if(response.isSuccessful) {
                this.selectedQueue = null;
                this.reload();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadCaseOwners(): Promise<void> {
        const includes: string[] = [];
        const currentOwnerId = this.context.case?.assignedTo?.id;

        try {
            this.state.beginLoad();

            const query = this.service.createLoadCaseOwnersQuery({ all: true }, includes);
            if(currentOwnerId) {
                query.filterIdentifier('id', currentOwnerId, 'ne');
            }
            const response = await this.service.get<CM.CaseOwner>(query);
            this.caseOwners = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async assignCase(): Promise<void> {
        if(!this.selectedOwner) { return; }

        const currentOwnerId = this.context.case?.assignedTo?.id;
        if(currentOwnerId === this.selectedOwner.id) {
            this.toaster.warn().message('Case is already assigned to that user');
            return;
        }

        const saveCase: CM.Case = {
            id: this.caseId,
            assignedTo: getReference(this.selectedOwner)
        }

        try {
            this.state.beginSave();
            const response = await this.service.assignCase(saveCase);
            this.toaster.standard.assigned('cm:case.$', response.isSuccessful);
            if(response.isSuccessful) {
                this.context.case.assignedTo = deepClone(this.selectedOwner);
                this.selectedOwner = null;
                this.$assignmentHistory.reload();
                this.loadCaseOwners();
                this.events.publish(new CaseChangeEvent({ id: this.caseId }));
            }
        } catch(error) {
            this.toaster.standard.assigned('cm:case.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

}
